import ExifReader from "exifreader";

export async function readPhotoGpsLocation(file, { loadTags = loadExifTags } = {}) {
  const buffer = await file.arrayBuffer();
  const tags = await loadTags(buffer);
  return gpsLocationFromTags(tags);
}

export async function loadExifTags(buffer) {
  return ExifReader.load(buffer, { expanded: true, computed: true });
}

export function gpsLocationFromTags(tags) {
  const groupedLatitude = numberOrNull(tags?.gps?.Latitude);
  const groupedLongitude = numberOrNull(tags?.gps?.Longitude);
  if (validLatitude(groupedLatitude) && validLongitude(groupedLongitude)) {
    return { latitude: groupedLatitude, longitude: groupedLongitude };
  }

  const latitude = gpsCoordinateToDecimal(gpsTag(tags, "GPSLatitude"), gpsTag(tags, "GPSLatitudeRef"));
  const longitude = gpsCoordinateToDecimal(gpsTag(tags, "GPSLongitude"), gpsTag(tags, "GPSLongitudeRef"));
  if (!validLatitude(latitude) || !validLongitude(longitude)) return null;
  return { latitude, longitude };
}

export function gpsCoordinateToDecimal(coordinateTag, refTag) {
  const parts = dmsParts(coordinateTag);
  const ref = gpsRefValue(refTag);
  if (!parts || !ref) return null;

  const [degrees, minutes, seconds] = parts;
  if (
    degrees < 0 ||
    minutes < 0 ||
    minutes >= 60 ||
    seconds < 0 ||
    seconds >= 60
  ) {
    return null;
  }

  const absolute = degrees + (minutes / 60) + (seconds / 3600);
  return ref === "S" || ref === "W" ? -absolute : absolute;
}

function gpsTag(tags, name) {
  return tags?.[name] || tags?.exif?.[name] || null;
}

function dmsParts(tag) {
  const value = tagValue(tag);
  if (Array.isArray(value)) {
    const parts = value.map(rationalNumber);
    return parts.length === 3 && parts.every((part) => part !== null) ? parts : null;
  }

  if (typeof value === "string") {
    const parts = value.match(/-?\d+(?:\.\d+)?/g)?.map(Number) || [];
    return parts.length === 3 && parts.every(Number.isFinite) ? parts : null;
  }

  return null;
}

function gpsRefValue(tag) {
  const value = tagValue(tag);
  const ref = Array.isArray(value) ? value[0] : value;
  const normalized = String(ref || "").trim().toUpperCase();
  return ["N", "S", "E", "W"].includes(normalized) ? normalized : null;
}

function tagValue(tag) {
  if (!tag || typeof tag !== "object" || Array.isArray(tag)) return tag;
  if (Object.hasOwn(tag, "computed")) return tag.computed;
  if (Object.hasOwn(tag, "value")) return tag.value;
  return tag.description;
}

function rationalNumber(value) {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string" && value.trim()) return numberOrNull(Number(value));
  if (Array.isArray(value) && value.length >= 2) {
    const numerator = numberOrNull(value[0]);
    const denominator = numberOrNull(value[1]);
    return denominator ? numerator / denominator : null;
  }
  if (value && typeof value === "object") {
    const numerator = numberOrNull(value.numerator ?? value[0]);
    const denominator = numberOrNull(value.denominator ?? value[1]);
    return denominator ? numerator / denominator : null;
  }
  return null;
}

function numberOrNull(value) {
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
}

function validLatitude(value) {
  return typeof value === "number" && Number.isFinite(value) && value >= -90 && value <= 90;
}

function validLongitude(value) {
  return typeof value === "number" && Number.isFinite(value) && value >= -180 && value <= 180;
}
